import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import cn from 'classnames';
import { TweenLite, Linear } from 'gsap';
import {
  getCarouselSlideCount,
  getCarouselSlideDuration,
  getCarouselSlideIndex,
  getCarouselVideoDuration,
  getCarouselVideoProgress,
  getSliceActiveMenuIndex,
  isCarouselPaused,
  isCarouselVideoPlaying,
} from '../../selectors';
import calculateProgressArcPath from '../../helpers/calculateProgressArcPath';
import { carouselSlideChangedCreator } from '../../actions';
import theme from '../../styles/theme';
import range from '../../helpers/range';

class Pips extends PureComponent {
  state = {
    progress: 0,
  };

  componentDidMount() {
    const self = this;
    const tweener = {
      get progress() {
        return self.state.progress;
      },
      set progress(progress) {
        self.setState({ progress });
      },
    };
    const { isPaused, slideDuration } = this.props;
    this.progressTween = TweenLite.to(tweener, slideDuration, {
      progress: 1,
      ease: Linear.easeNone,
    });
    if (isPaused) {
      this.progressTween.pause();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      slideIndex,
      progress,
    } = this.props;
    if (prevProps.slideIndex !== slideIndex) {
      this.slideChanged();
    }
    this.synchronizeProgressTween(prevProps);
    // if the progress `prop` changed, that means we got an update from a video that is currently
    // playing, so we need to force-update the tween to keep it in sync with video playback
    if (prevProps.progress !== progress) {
      this.progressTween.progress(progress);
    }
  }

  componentWillUnmount() {
    this.progressTween.kill();
  }

  slideChanged() {
    const { isPaused } = this.props;
    if (isPaused) {
      this.progressTween.pause(0);
    } else {
      this.progressTween.restart();
    }
  }

  synchronizeProgressTween() {
    const {
      isPaused,
      slideDuration,
    } = this.props;
    if (isPaused !== this.progressTween.paused()) {
      if (isPaused) {
        this.progressTween.pause();
      } else {
        this.progressTween.restart();
      }
    }
    if (slideDuration !== this.progressTween.duration()) {
      this.progressTween.duration(slideDuration);
    }
  }

  renderIcon(i) {
    const {
      classes,
      slideIndex,
    } = this.props;
    const { progress } = this.state;
    const isCurrent = i === slideIndex;
    return (
      <svg className={classes.svg} viewBox="0 0 20 20">
        <circle
          cx="10"
          cy="10"
          r="3"
          className={cn(classes.pip, isCurrent && classes.currentPip)}
        />
        {isCurrent && (progress < 1 ? (
          <path
            className={classes.progressCircle}
            d={calculateProgressArcPath({
              startAngle: 0, endAngle: progress * 360, x: 10, y: 10, radius: 9,
            })}
          />
        ) : <circle className={classes.progressCircle} cx="10" cy="10" r="9" />)}
      </svg>
    );
  }

  render() {
    const {
      classes,
      className,
      edgeToEdge,
      onPipClick,
      slideCount,
    } = this.props;
    const slideIndices = range(slideCount);
    return (
      <div className={cn(classes.container, className, edgeToEdge && classes.edgeToEdge)}>
        {slideIndices.map(i => (
          <button
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className={classes.button}
            type="button"
            onClick={() => onPipClick(i)}
          >
            {this.renderIcon(i)}
          </button>
        ))}
      </div>
    );
  }
}

Pips.propTypes = {
  carouselId: PropTypes.number.isRequired, // eslint-disable-line react/no-unused-prop-types
  className: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  edgeToEdge: PropTypes.bool,
  onPipClick: PropTypes.func.isRequired,
  slideCount: PropTypes.number.isRequired,
  slideDuration: PropTypes.number.isRequired,
  slideIndex: PropTypes.number.isRequired,
  isPaused: PropTypes.bool.isRequired,
  progress: PropTypes.number.isRequired, // eslint-disable-line react/no-unused-prop-types
};

Pips.defaultProps = {
  className: undefined,
  edgeToEdge: true,
};

const mapStateToProps = (state, { carouselId }) => {
  const isMenuOpen = getSliceActiveMenuIndex(state, carouselId) >= 0;
  const isVideoPlaying = isCarouselVideoPlaying(state, carouselId);
  return {
    slideCount: getCarouselSlideCount(state, carouselId),
    slideDuration: isVideoPlaying
      ? getCarouselVideoDuration(state, carouselId)
      : getCarouselSlideDuration(state, carouselId) / 1000,
    slideIndex: getCarouselSlideIndex(state, carouselId),
    isPaused: isVideoPlaying ? false : (isMenuOpen || isCarouselPaused(state, carouselId)),
    progress: isVideoPlaying ? getCarouselVideoProgress(state, carouselId) : 0,
  };
};

const mapDispatchToProps = (dispatch, { carouselId }) => ({
  onPipClick: slideIndex => dispatch(carouselSlideChangedCreator(carouselId, slideIndex)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),

  injectSheet({
    container: {
      fontSize: 20,
      lineHeight: '1.2em',
    },
    button: {
      cursor: 'pointer',
      outline: 0,
      '&:not(.container-button)': { // specificity needed to beat existing styles in the page
        fontSize: 'inherit',
        lineHeight: 'inherit',
        padding: '0.1em',
        minHeight: 0,
        margin: 0,
        transition: 'color 0.2s ease-out',
        '&,&:hover,&:focus': {
          background: 'transparent none',
          border: 0,
          color: 'inherit',
        },
        '&:hover,&:focus': {
          '& $pip': {
            fill: 'currentColor',
            fillOpacity: 0.5,
          },
          '& $currentPip': {
            fillOpacity: 1,
          },
        },
      },
    },
    edgeToEdge: {
      [theme.breakpoints.up('xxl')]: {
        fontSize: 26,
      },
    },
    svg: {
      display: 'block',
      width: '1em',
      height: '1em',
    },
    pip: {
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: '0.05em',
    },
    currentPip: {
      fill: 'currentColor',
    },
    progressCircle: {
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: '0.05em',
    },
  }),
)(Pips);
